import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { API } from '../lib/api';

function UserProfile({ onClose }) {
  const [currentUser, setCurrentUser] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showChangeUsername, setShowChangeUsername] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [passwordForUsername, setPasswordForUsername] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setCurrentUser(res.data.username);
    } catch (e) {
      console.error(e);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 4) {
      toast.error('Le mot de passe doit contenir au moins 4 caractères');
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Mot de passe modifié avec succès');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleChangeUsername = async () => {
    if (!newUsername.trim()) {
      toast.error('Le nom d\'utilisateur ne peut pas être vide');
      return;
    }

    if (newUsername.length < 3) {
      toast.error('Le nom d\'utilisateur doit contenir au moins 3 caractères');
      return;
    }

    try {
      const res = await axios.post(`${API}/auth/change-username`, {
        new_username: newUsername,
        password: passwordForUsername
      });
      toast.success('Nom d\'utilisateur modifié avec succès');
      localStorage.setItem('auth_token', res.data.access_token);
      setShowChangeUsername(false);
      setNewUsername('');
      setPasswordForUsername('');
      setCurrentUser(res.data.new_username);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 fade-in">
      <div className="bg-[#0a0a0a] border border-[#262626] p-8 max-w-md w-full corner-decoration relative slide-in">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-primary font-black text-xl uppercase">PROFIL UTILISATEUR</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-white font-secondary text-xs uppercase"
          >
            FERMER
          </button>
        </div>

        <div className="mb-6">
          <p className="font-secondary text-xs text-muted-foreground uppercase mb-2">Utilisateur actuel</p>
          <p className="font-secondary text-lg font-bold text-[#ff4500]">{currentUser}</p>
        </div>

        {!showChangePassword && !showChangeUsername && (
          <div className="space-y-4">
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full bg-[#0a0a0a] border border-[#ff4500] text-[#ff4500] font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#ff4500]/10 transition-colors font-secondary text-sm"
              data-testid="change-password-btn"
            >
              Modifier le mot de passe
            </button>
            <button
              onClick={() => setShowChangeUsername(true)}
              className="w-full bg-[#0a0a0a] border border-[#00ff9d] text-[#00ff9d] font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#00ff9d]/10 transition-colors font-secondary text-sm"
              data-testid="change-username-btn"
            >
              Modifier le nom d'utilisateur
            </button>
          </div>
        )}

        {showChangePassword && (
          <div className="space-y-4">
            <div>
              <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">Mot de passe actuel</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary focus:outline-none transition-colors"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">Nouveau mot de passe</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary focus:outline-none transition-colors"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary focus:outline-none transition-colors"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleChangePassword}
                className="flex-1 bg-[#ff4500] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#ff4500]/90 transition-colors font-secondary text-sm"
                data-testid="confirm-change-password"
              >
                Confirmer
              </button>
              <button
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="flex-1 border border-white/10 text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white/5 transition-colors font-secondary text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {showChangeUsername && (
          <div className="space-y-4">
            <div>
              <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">Nouveau nom d'utilisateur</label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary focus:outline-none transition-colors"
                data-testid="new-username-input"
              />
            </div>
            <div>
              <label className="font-secondary text-xs uppercase text-muted-foreground block mb-2">Mot de passe (pour confirmation)</label>
              <input
                type="password"
                value={passwordForUsername}
                onChange={(e) => setPasswordForUsername(e.target.value)}
                className="w-full bg-[#1f1f22] border-b border-white/10 focus:border-[#ff4500] px-4 py-3 text-sm font-secondary focus:outline-none transition-colors"
                data-testid="password-for-username-input"
              />
            </div>
            <div className="flex gap-4 mt-6">
              <button
                onClick={handleChangeUsername}
                className="flex-1 bg-[#00ff9d] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#00ff9d]/90 transition-colors font-secondary text-sm"
                data-testid="confirm-change-username"
              >
                Confirmer
              </button>
              <button
                onClick={() => {
                  setShowChangeUsername(false);
                  setNewUsername('');
                  setPasswordForUsername('');
                }}
                className="flex-1 border border-white/10 text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white/5 transition-colors font-secondary text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserProfile;